import { useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import { api } from '../lib/api'
import { PageHeader, Button } from '../components/ui'

const PLANS = [
  {
    key: 'free', name: 'Free', price: '$0', period: '/mo',
    features: ['500 feedback/mo', '3 projects', '100MB storage', 'Email support'],
  },
  {
    key: 'indie', name: 'Indie', price: '$9', period: '/mo',
    features: ['5,000 feedback/mo', '10 projects', '1GB storage', 'Priority email', '7-day history'],
  },
  {
    key: 'studio', name: 'Studio', price: '$29', period: '/mo',
    features: ['50,000 feedback/mo', 'Unlimited projects', '10GB storage', 'Slack support', '30-day history', '5 team seats'],
  },
] as const

export function Billing() {
  const { user } = useAuth()
  const plan = user?.plan ?? 'free'
  const [loading, setLoading] = useState<'indie' | 'studio' | null>(null)
  const [checkoutError, setCheckoutError] = useState<string | null>(null)

  // Show a success banner when redirected back from Dodo checkout
  const params = new URLSearchParams(window.location.search)
  const sessionSuccess = params.get('session') === 'success'

  async function handleUpgrade(targetPlan: 'indie' | 'studio') {
    setLoading(targetPlan)
    setCheckoutError(null)
    try {
      const { checkout_url } = await api.billing.createCheckout(targetPlan)
      window.location.href = checkout_url
    } catch (err) {
      setCheckoutError(err instanceof Error ? err.message : 'Something went wrong')
      setLoading(null)
    }
  }

  return (
    <div style={{ padding: '40px 48px', maxWidth: 860 }}>
      <PageHeader title="Billing" />

      {sessionSuccess && (
        <div style={{
          background: '#f0faf4', border: '1px solid #a3d9b1', borderRadius: 10,
          padding: '14px 20px', marginBottom: 24, color: '#1a6636', fontSize: 14, fontWeight: 600,
        }}>
          Payment successful — your plan has been activated. It may take a moment to reflect.
        </div>
      )}

      {checkoutError && (
        <div style={{
          background: '#fff0f0', border: '1px solid #f5c0c0', borderRadius: 10,
          padding: '14px 20px', marginBottom: 24, color: '#b91c1c', fontSize: 14,
        }}>
          {checkoutError}
        </div>
      )}

      {/* Current plan banner */}
      <div style={{
        background: '#ffffff', borderRadius: 12,
        boxShadow: '0 2px 8px rgba(25,28,30,0.06)',
        padding: '20px 24px', marginBottom: 28,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        borderLeft: '4px solid #3525cd',
      }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4, color: '#191c1e', fontFamily: 'Manrope, sans-serif' }}>
            You're on the <span style={{ color: '#3525cd' }}>{plan.charAt(0).toUpperCase() + plan.slice(1)}</span> plan
          </div>
          <div style={{ color: '#777587', fontSize: 14 }}>
            {plan === 'free' ? 'Upgrade to unlock more features' : 'Manage your subscription below'}
          </div>
        </div>
        {plan !== 'studio' && (
          <Button
            variant="outline"
            onClick={() => handleUpgrade(plan === 'free' ? 'indie' : 'studio')}
            disabled={loading !== null}
          >
            {loading === (plan === 'free' ? 'indie' : 'studio')
              ? 'Redirecting…'
              : `Upgrade to ${plan === 'free' ? 'Indie' : 'Studio'} →`}
          </Button>
        )}
      </div>

      {/* Plan cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
        {PLANS.map(p => {
          const isCurrent = p.key === plan
          const isPopular = p.key === 'indie'
          const isPaid = p.key === 'indie' || p.key === 'studio'
          const isUpgrade = !isCurrent && (
            (plan === 'free' && isPaid) ||
            (plan === 'indie' && p.key === 'studio')
          )

          return (
            <div key={p.key} style={{
              background: '#ffffff',
              borderRadius: 12,
              padding: 24,
              display: 'flex', flexDirection: 'column', gap: 20,
              boxShadow: isCurrent
                ? '0 0 0 2px #3525cd, 0 12px 32px rgba(53,37,205,0.12)'
                : '0 2px 8px rgba(25,28,30,0.06)',
              position: 'relative',
            }}>
              {isPopular && (
                <div style={{
                  position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)',
                  background: '#3525cd', color: '#ffffff', fontSize: 11, fontWeight: 700,
                  padding: '3px 12px', borderRadius: 999, letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                }}>Popular</div>
              )}

              <div>
                <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 8, color: '#464555', textTransform: 'uppercase', letterSpacing: '0.06em' } as React.CSSProperties}>{p.name}</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 2 }}>
                  <span style={{ fontSize: 34, fontWeight: 800, color: '#191c1e', fontFamily: 'Manrope, sans-serif' }}>{p.price}</span>
                  <span style={{ fontSize: 14, color: '#777587' }}>{p.period}</span>
                </div>
              </div>

              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {p.features.map(f => (
                  <li key={f} style={{ fontSize: 13, color: '#464555', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{
                      width: 18, height: 18, borderRadius: '50%',
                      background: '#c8f0d8', color: '#003920',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 10, fontWeight: 700, flexShrink: 0,
                    }}>✓</span>
                    {f}
                  </li>
                ))}
              </ul>

              {isCurrent ? (
                <div style={{
                  fontSize: 13, color: '#3525cd', fontWeight: 600,
                  display: 'flex', alignItems: 'center', gap: 6,
                  background: 'rgba(53,37,205,0.06)', padding: '8px 12px',
                  borderRadius: 8, marginTop: 'auto',
                }}>
                  <span style={{ fontSize: 14 }}>✓</span> Current plan
                </div>
              ) : isUpgrade && isPaid ? (
                <Button
                  variant={p.key === 'studio' ? 'primary' : 'outline'}
                  size="sm"
                  style={{ marginTop: 'auto', justifyContent: 'center' }}
                  onClick={() => handleUpgrade(p.key as 'indie' | 'studio')}
                  disabled={loading !== null}
                >
                  {loading === p.key ? 'Redirecting…' : `Upgrade to ${p.name}`}
                </Button>
              ) : (
                <div style={{
                  fontSize: 13, color: '#aaa9b8',
                  padding: '8px 12px', marginTop: 'auto',
                  textAlign: 'center',
                }}>
                  {p.key === 'free' ? 'Free forever' : 'Lower plan'}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
